import React from 'react';
import { Panel } from '../layout/Panel';
import { sectorData } from '../../data/terminalData';

const getSignalStyle = (signal: string): string => {
  switch (signal) {
    case 'STRONG BUY':
      return 'bg-data-green text-black';
    case 'BUY':
      return 'bg-data-green/20 text-data-green border border-data-green/30';
    case 'HOLD':
      return 'bg-data-amber/20 text-data-amber border border-data-amber/30';
    case 'SELL':
      return 'bg-data-red/20 text-data-red border border-data-red/30';
    default:
      return 'bg-gray-600 text-gray-300';
  }
};

const getCompetitionColor = (competition: string): string => {
  switch (competition) {
    case 'V.LOW':
    case 'LOW':
      return 'text-data-green';
    case 'MED':
      return 'text-data-amber';
    case 'HIGH':
      return 'text-data-red';
    default:
      return 'text-gray-400';
  }
};

interface SectorPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const SectorPanel: React.FC<SectorPanelProps> = ({ isMinimized, onMinimize }) => {
  // Prepare exportable data
  const exportData = sectorData.map(s => ({
    sector: s.sector,
    marketSize: s.size,
    projected2028: s.proj2028,
    cagr: s.cagr,
    competition: s.competition,
    timing: s.timing,
    signal: s.signal,
  }));

  return (
    <Panel id="sectors" title="Sectors" subtitle="Investment Thesis" status="static" data={exportData} isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full p-2">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 border-b border-terminal-border">
              <th className="text-left py-2 font-medium">SECTOR</th>
              <th className="text-right py-2 font-medium">SIZE</th>
              <th className="text-right py-2 font-medium">'28E</th>
              <th className="text-right py-2 font-medium">CAGR</th>
              <th className="text-center py-2 font-medium">COMP</th>
              <th className="text-center py-2 font-medium">TIMING</th>
              <th className="text-center py-2 font-medium">SIGNAL</th>
            </tr>
          </thead>
          <tbody>
            {sectorData.map((s) => (
              <tr
                key={s.sector}
                className="border-b border-terminal-border/30 hover:bg-data-amber/5 transition-colors"
              >
                <td className="py-3 text-white font-semibold">{s.sector}</td>
                <td className="py-3 text-right text-gray-300 font-mono">${s.size}B</td>
                <td className="py-3 text-right text-gray-300 font-mono">${s.proj2028}B</td>
                <td className="py-3 text-right text-data-green font-mono font-bold">{s.cagr}%</td>
                <td className={`py-3 text-center font-medium ${getCompetitionColor(s.competition)}`}>
                  {s.competition}
                </td>
                <td className="py-3 text-center text-data-amber font-medium">{s.timing}</td>
                <td className="py-3 text-center">
                  <span className={`px-2 py-1 rounded text-xs font-bold ${getSignalStyle(s.signal)}`}>
                    {s.signal}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Legend */}
        <div className="mt-auto pt-3 flex gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-data-green" />
            <span>Strong Buy</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-data-amber" />
            <span>Hold</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-gray-400">COMP</span>
            <span>= Competition</span>
          </div>
        </div>
      </div>
    </Panel>
  );
};

export default SectorPanel;
